import { cn } from "@/lib/utils"
import { ArrowLeft } from "lucide-react"

interface StudioHeaderProps {
  /**
   * Optional stepper/wizard progress rendered flush above the metadata bar
   * (e.g. the declaration review flow's ReviewSteps). Single-step flows omit it.
   */
  stepper?: React.ReactNode
  /** Back affordance: when provided, renders "← label" + a divider before the title. */
  onBack?: () => void
  backLabel?: React.ReactNode
  /** Main title — supplier name, reference, etc. (white, semibold). */
  title: React.ReactNode
  /** Context line after the title — typically a small icon + muted text. */
  context?: React.ReactNode
  /** Right-aligned content: badges and/or action buttons. */
  children?: React.ReactNode
  className?: string
}

/**
 * Unified Supplier Studio header. Both the multi-step declaration review flow
 * and the single-step inquiry flows render through this so the bar stays in
 * sync (height, divider, back affordance, title + context, right-side badges).
 * Pass `stepper` to render wizard progress above the bar.
 */
export function StudioHeader({
  stepper,
  onBack,
  backLabel,
  title,
  context,
  children,
  className,
}: StudioHeaderProps) {
  return (
    <>
      {stepper}
      <div
        className={cn(
          "flex shrink-0 items-center gap-3 border-b border-grey4 px-6 py-3",
          className,
        )}
      >
        {onBack && (
          <>
            <button
              onClick={onBack}
              className="flex items-center gap-1 text-xs text-grey5 hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {backLabel}
            </button>
            <span className="h-4 w-px bg-grey4" />
          </>
        )}
        <span className="whitespace-nowrap text-sm font-semibold text-white">
          {title}
        </span>
        {context && (
          <span className="flex min-w-0 items-center gap-1.5 text-xs text-grey5">
            {context}
          </span>
        )}
        <span className="flex-1" />
        {children}
      </div>
    </>
  )
}
